import React, { useRef, useCallback, useState, useEffect } from "react";
import { motion, useMotionValue, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Pause, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { Bubbles, Caustics } from "@/components/SwimmingFX";
import Confetti from "react-confetti";
import Countdown from "@/components/Countdown";
import { OFFICIAL_LOGO, EVENT_TAGLINE, INDIVIDUAL_EVENT_COUNT, RELAY_EVENT_COUNT, AGE_CATEGORY_COUNT, COMPETITION_DAYS } from "@/lib/brand";

const EVENT_DATE = "2026-10-04T10:00:00+05:30";
// Account registration closes on 29 Sep 2026 23:59 IST
const currentHour = new Date().getHours();
const REGISTRATION_CLOSED = currentHour < 6 || currentHour >= 18;
const HERO_POSTER = "https://images.pexels.com/photos/1263348/pexels-photo-1263348.jpeg";

export default function CinematicHero() {
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowDim, setWindowDim] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setWindowDim({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    
    // Give a nice burst for 5 seconds then stop adding new pieces
    const timer = setTimeout(() => setShowConfetti(false), 5000);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, []);

  const ref = useRef(null);
  const videoRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const px = useTransform(mx, [0, 1], [-15, 15]);
  const py = useTransform(my, [0, 1], [-12, 12]);
  const spotlightBg = useTransform(
    [mx, my],
    ([x, y]) => `radial-gradient(circle at ${x * 100}% ${y * 100}%, rgba(0,240,255,0.18), transparent 45%)`
  );
  const prefersReducedMotion = useReducedMotion();

  const [videoPlaying, setVideoPlaying] = useState(false);
  const toggleVideo = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setVideoPlaying(true); } else { v.pause(); setVideoPlaying(false); }
  }, []);

  const onMouse = useCallback((e) => {
    if (!ref.current || window.matchMedia("(hover: none)").matches) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  }, [mx, my]);

  const title = ["ASIAN MASTERS", "SWIMMING", "CHAMPIONSHIPS", "INDIA · 2026"];

  return (
    <section
      ref={ref}
      onMouseMove={onMouse}
      data-testid="hero-section"
      className="relative min-h-dvh flex items-center overflow-hidden pt-20 sm:pt-24 pb-20 sm:pb-8"
    >
      {/* Parallax background video */}
      <motion.div
        style={{ x: px, y: py, scale: 1.08 }}
        className="absolute inset-0"
      >
        <video
          ref={videoRef}
          autoPlay={!prefersReducedMotion}
          loop
          muted
          playsInline
          poster={HERO_POSTER}
          className="w-full h-full object-cover"
          data-testid="hero-image"
          onPlay={() => setVideoPlaying(true)}
          onPause={() => setVideoPlaying(false)}
        >
          <source src="/hero_compressed.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* WCAG 2.2.2: auto-playing looping video needs a visible, keyboard-reachable pause control */}
      <button
        type="button"
        onClick={toggleVideo}
        aria-label={videoPlaying ? "Pause background video" : "Play background video"}
        data-testid="hero-video-toggle"
        className="absolute bottom-4 right-4 z-10 w-9 h-9 rounded-full bg-[#0B1429]/70 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 transition-colors"
      >
        {videoPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>

      {/* Dark gradients */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1429] via-[#0B1429]/85 to-[#0B1429]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1429] via-transparent to-transparent" />
      <Caustics opacity={0.2} />
      <Bubbles count={18} />

      {/* Animated radial spotlight (mouse-following) — skipped for reduced motion */}
      {!prefersReducedMotion && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ background: spotlightBg }}
        />
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid md:grid-cols-12 gap-8 md:gap-10 items-center w-full">
        <div className="md:col-span-7 min-w-0">


          {/* Word-by-word headline */}
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-[5rem] leading-[0.95] tracking-tight">
            {title.map((word, i) => (
              <motion.span
                key={word}
                initial={{ opacity: 0, y: 50, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === 3 ? "text-[#00F0FF]" : ""}`}
              >
                {word}
              </motion.span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.6 }}
            className="mt-5 sm:mt-7 text-base md:text-xl text-[#CBD5E1] max-w-xl leading-relaxed"
          >
            India hosts Asia's largest Masters swimming gathering — 7 days of elite racing at the Centre for Sports Excellence, Bengaluru.
            <span className="block mt-3 text-[#00F0FF] font-semibold italic">
              "{EVENT_TAGLINE}"
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-6 sm:mt-8 flex flex-wrap gap-3 sm:gap-4"
            style={{ touchAction: "manipulation" }}
          >
            {!REGISTRATION_CLOSED && (
              <Link to="/register" data-testid="hero-cta-register" className="btn-primary group min-h-[44px]">
                Register as Athlete
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            <Link to="/schedule" data-testid="hero-cta-schedule" className="btn-secondary min-h-[44px]">
              Order of Events
            </Link>
          </motion.div>

          {/* Animated mini-stats row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-6 sm:mt-10 grid grid-cols-2 xs:grid-cols-4 sm:flex sm:flex-wrap items-center gap-x-4 sm:gap-x-8 gap-y-4 text-sm"
          >
            {[
              { v: String(INDIVIDUAL_EVENT_COUNT), l: "Events" },
              { v: String(AGE_CATEGORY_COUNT), l: "Categories" },
              { v: String(RELAY_EVENT_COUNT), l: "Relays" },
              { v: String(COMPETITION_DAYS), l: "Days" },
            ].map((s) => (
              <div key={s.l} className="border-l border-[#00F0FF]/30 pl-4 min-w-0">
                <div className="font-display font-black text-2xl text-white">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] truncate">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Countdown card with masked reveal */}
        <motion.div
          initial={{ opacity: 0, clipPath: "circle(0% at 50% 50%)" }}
          animate={{ opacity: 1, clipPath: "circle(100% at 50% 50%)" }}
          transition={{ delay: 0.8, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 md:mt-0 md:col-span-5 min-w-0 flex flex-col gap-4"
        >
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.6 }}
            className="flex justify-center"
          >
            <Confetti
              width={windowDim.width}
              height={windowDim.height}
              recycle={showConfetti}
              numberOfPieces={showConfetti ? 200 : 0}
              style={{ position: 'fixed', top: 0, left: 0, zIndex: 50, pointerEvents: 'none' }}
            />
            <div
              data-testid="registration-status-badge"
              className="relative inline-flex items-center gap-2.5 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-[#0B1429]/70 border border-[#00E676]/40 backdrop-blur-md shadow-[0_0_15px_rgba(0,230,118,0.2)] overflow-hidden max-w-full"
            >
              <span className="w-2 h-2 rounded-full bg-[#00E676] shrink-0 animate-pulse" />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-wide sm:tracking-widest text-white/85 text-center">
                Best of luck to all swimmers competing in the championships!
              </span>
              <span className="badge-shimmer" />
            </div>
          </motion.div>

          <div
            className="relative rounded-2xl p-6 md:p-8 overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(20,32,64,0.65) 0%, rgba(11,20,41,0.4) 100%)",
              border: "1px solid rgba(0,240,255,0.25)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              boxShadow: "0 24px 80px rgba(0,240,255,0.12), inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            {/* Animated aurora orb — static (no pulse) for reduced motion */}
            <motion.div
              className="absolute -right-20 -top-20 w-72 h-72 rounded-full pointer-events-none"
              animate={prefersReducedMotion ? { opacity: 0.25 } : { scale: [1, 1.15, 1], opacity: [0.18, 0.35, 0.18] }}
              transition={prefersReducedMotion ? undefined : { duration: 5, repeat: Infinity, ease: "easeInOut" }}
              style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)", filter: "blur(20px)" }}
            />
            <motion.div
              className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full pointer-events-none"
              animate={prefersReducedMotion ? { opacity: 0.18 } : { scale: [1.1, 1, 1.1], opacity: [0.12, 0.25, 0.12] }}
              transition={prefersReducedMotion ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 70%)", filter: "blur(20px)" }}
            />

            <div className="relative">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-semibold text-[#94A3B8]">Championship Begins In</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-semibold">Live</span>
                </div>
              </div>

              <Countdown target={EVENT_DATE} />
              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Opening</div>
                  <div className="text-sm font-semibold">Oct 4, 2026 · 10:00 AM IST</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Venue</div>
                  <div className="text-sm font-semibold text-[#00F0FF]">Bengaluru</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator — desktop only to avoid overlapping CTAs on mobile */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="hidden sm:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#94A3B8]"
      >
        <div className="text-[10px] uppercase tracking-[0.3em]">Scroll</div>
        <motion.div
          animate={prefersReducedMotion ? { y: 4 } : { y: [0, 10, 0] }}
          transition={prefersReducedMotion ? undefined : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#00F0FF] to-transparent"
        />
      </motion.div>
    </section>
  );
}
